import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthState } from '../../types';
import { authService, LoginRequest, RegisterRequest } from '../services/authService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (phone: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
}

interface RegisterData {
  name: string;
  phone: string;
  password: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    loading: true,
    error: null,
  });

  // Check for existing session on initial load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check for stored token and decode user from JWT claims
        const token = localStorage.getItem('authToken');
        
        if (token && !authService.isTokenExpired()) {
          const user = authService.getUserFromToken();
          if (user) {
            setAuthState({
              user,
              isAuthenticated: true,
              loading: false,
              error: null,
            });
          } else {
            localStorage.removeItem('authToken');
            setAuthState(prev => ({ ...prev, loading: false }));
          }
        } else {
          // Clear invalid tokens
          localStorage.removeItem('authToken');
          setAuthState(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        localStorage.removeItem('authToken');
        setAuthState({
          user: null,
          isAuthenticated: false,
          loading: false,
          error: 'Error al verificar la sesión',
        });
      }
    };

    checkAuth();
  }, []);

  const login = async (phone: string, password: string) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const credentials: LoginRequest = { telefono: phone, password };
      const response = await authService.login(credentials);
      const user = authService.getUserFromToken();
      
      setAuthState({
        user: user || response.user,
        isAuthenticated: true,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error al iniciar sesión. Verifica tus credenciales.';
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
      throw error;
    }
  };

  const register = async (userData: RegisterData) => {
    setAuthState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const registerData: RegisterRequest = {
        telefono: userData.phone,
        password: userData.password,
        nombreUsuario: userData.name,
      };
      
      const response = await authService.register(registerData);
      const user = authService.getUserFromToken();
      
      setAuthState({
        user: user || response.user,
        isAuthenticated: true,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error al registrar el usuario. Intenta nuevamente.';
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setAuthState({
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user: authState.user,
        isAuthenticated: authState.isAuthenticated,
        loading: authState.loading,
        error: authState.error,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
